import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import type { BillingPocket, BillableMeterV2, BuildingCommonArea, PocketMeter } from '../api/types';
import MeterTypeIcon from '../components/MeterTypeIcon';
import BuildingReport from '../components/BuildingReport';
import ReportSchedulePanel from '../components/ReportSchedulePanel';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { DataTableColumn } from '../components/ui';

interface BuildingAsset {
  id: string;
  name: string;
}

export default function BuildingDetail() {
  const { buildingId } = useParams<{ buildingId: string }>();
  // Land on Pockets by default — it renders instantly from the pockets list
  // that already loaded with the page. Report does a per-pocket aggregation
  // loop in the API that takes a few seconds; users explicitly click into it
  // when they want the consolidated view rather than waiting on every visit.
  const [activeTab, setActiveTab] = useState<'report' | 'pockets' | 'settings'>('pockets');
  const [pockets, setPockets] = useState<BillingPocket[]>([]);
  const [buildings, setBuildings] = useState<BuildingAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pocket expansion
  const [expandedPocketId, setExpandedPocketId] = useState<string | null>(null);
  const [expandedPocket, setExpandedPocket] = useState<BillingPocket | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [removingMeterId, setRemovingMeterId] = useState<string | null>(null);

  // Create Pocket modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', shop_no: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit Pocket modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    shop_no: '',
    account_number: '',
    ca_percent_kwh: '0',
    ca_percent_kl: '0',
  });
  const [editingPocketId, setEditingPocketId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Add Meters modal
  const [showAddMetersModal, setShowAddMetersModal] = useState(false);
  const [availableMeters, setAvailableMeters] = useState<BillableMeterV2[]>([]);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [meterSearch, setMeterSearch] = useState('');
  const [selectedMeterIds, setSelectedMeterIds] = useState<Set<string>>(new Set());
  const [addingMeters, setAddingMeters] = useState(false);

  // Building Common Area meters (Settings tab) — registered CA meters drive
  // the Supply classification on the Building Report.
  const [caMeters, setCaMeters] = useState<BuildingCommonArea[]>([]);
  const [caLoading, setCaLoading] = useState(false);
  const [caError, setCaError] = useState<string | null>(null);
  const [caAddOpen, setCaAddOpen] = useState(false);
  const [caAvailableMeters, setCaAvailableMeters] = useState<BillableMeterV2[]>([]);
  const [caSearch, setCaSearch] = useState('');
  const [caSelectedMeterId, setCaSelectedMeterId] = useState<string | null>(null);
  const [caDescription, setCaDescription] = useState('');
  const [caSaving, setCaSaving] = useState(false);
  const [caRemovingId, setCaRemovingId] = useState<string | null>(null);

  useEffect(() => {
    if (buildingId) {
      loadData();
    }
  }, [buildingId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [pocketsRes, buildingsRes] = await Promise.all([
        api.getPockets(),
        api.getBuildings(),
      ]);
      // Filter pockets to this building
      setPockets(pocketsRes.data.filter(p => p.building_asset_id === buildingId));
      setBuildings(buildingsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const buildingName = buildings.find(b => b.id === buildingId)?.name || 'Building';
  const totalMeters = pockets.reduce((sum, p) => sum + (p.meter_count || 0), 0);
  // Per-utility totals; both should sum to ~100% across consumer pockets in
  // the building when CA is fully allocated.
  const totalCaPercentKwh = pockets.reduce((sum, p) => sum + (p.ca_percent_kwh ?? p.ca_percent ?? 0), 0);
  const totalCaPercentKl  = pockets.reduce((sum, p) => sum + (p.ca_percent_kl  ?? p.ca_percent ?? 0), 0);

  // Load registered CA meters for this building.
  const loadCaMeters = async () => {
    if (!buildingId) return;
    try {
      setCaLoading(true);
      setCaError(null);
      const res = await api.getBuildingCommonArea(buildingId);
      setCaMeters(res.data);
    } catch (err) {
      setCaError(err instanceof Error ? err.message : 'Failed to load common-area meters');
    } finally {
      setCaLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'settings' && buildingId) {
      loadCaMeters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, buildingId]);

  const openCaAddModal = async () => {
    setCaAddOpen(true);
    setCaSelectedMeterId(null);
    setCaDescription('');
    setCaSearch('');
    try {
      // Pull all billable meters via the v2 endpoint (richer fields incl.
      // assigned_pocket_name). Bulk / CA meters may already sit inside a
      // pocket, so we don't filter on assignment here.
      const res = await api.getBillableMetersV2();
      setCaAvailableMeters(res.data);
    } catch (err) {
      setCaError(err instanceof Error ? err.message : 'Failed to load meter list');
    }
  };

  const handleAddCaMeter = async () => {
    if (!buildingId || !caSelectedMeterId) return;
    try {
      setCaSaving(true);
      setCaError(null);
      await api.addBuildingCommonArea(buildingId, {
        common_meter_asset_id: caSelectedMeterId,
        description: caDescription || undefined,
      });
      setCaAddOpen(false);
      await loadCaMeters();
    } catch (err) {
      setCaError(err instanceof Error ? err.message : 'Failed to register CA meter');
    } finally {
      setCaSaving(false);
    }
  };

  const handleRemoveCaMeter = async (caId: string) => {
    if (!buildingId) return;
    if (!confirm('Remove this common-area meter registration? Pockets containing it will no longer be classified as Supply.')) return;
    try {
      setCaRemovingId(caId);
      setCaError(null);
      await api.removeBuildingCommonArea(buildingId, caId);
      await loadCaMeters();
    } catch (err) {
      setCaError(err instanceof Error ? err.message : 'Failed to remove CA meter');
    } finally {
      setCaRemovingId(null);
    }
  };

  const caMeterIdsRegistered = new Set(caMeters.map(m => m.common_meter_asset_id));
  const caFilteredMeters = caAvailableMeters
    .filter(m => !caMeterIdsRegistered.has(m.meter_asset_id))
    .filter(m => {
      if (!caSearch) return true;
      const q = caSearch.toLowerCase();
      return (m.meter_name || '').toLowerCase().includes(q) || m.meter_asset_id.toLowerCase().includes(q);
    });

  const loadPocketDetail = async (id: string) => {
    try {
      setLoadingDetail(true);
      const res = await api.getPocket(id);
      setExpandedPocket(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pocket details');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleTogglePocket = async (pocket: BillingPocket) => {
    if (expandedPocketId === pocket.id) {
      setExpandedPocketId(null);
      setExpandedPocket(null);
    } else {
      setExpandedPocketId(pocket.id);
      await loadPocketDetail(pocket.id);
    }
  };

  const handleCreatePocket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buildingId) return;
    try {
      setCreating(true);
      setCreateError(null);
      await api.createPocket({
        name: createForm.name,
        description: createForm.description || undefined,
        building_asset_id: buildingId,
        shop_no: createForm.shop_no || undefined,
      });
      setShowCreateModal(false);
      setCreateForm({ name: '', description: '', shop_no: '' });
      await loadData();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create pocket');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenEditPocket = (pocket: BillingPocket) => {
    setEditingPocketId(pocket.id);
    setEditForm({
      name: pocket.name,
      description: pocket.description || '',
      shop_no: pocket.shop_no || '',
      account_number: pocket.account_number || '',
      // Backfill from legacy ca_percent for older pockets that haven't had
      // their per-utility values set explicitly yet.
      ca_percent_kwh: String(pocket.ca_percent_kwh ?? pocket.ca_percent ?? 0),
      ca_percent_kl:  String(pocket.ca_percent_kl  ?? pocket.ca_percent ?? 0),
    });
    setEditError(null);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPocketId) return;
    try {
      setSaving(true);
      setEditError(null);
      await api.updatePocket(editingPocketId, {
        name: editForm.name,
        description: editForm.description || undefined,
        shop_no: editForm.shop_no || undefined,
        account_number: editForm.account_number || undefined,
        ca_percent_kwh: parseFloat(editForm.ca_percent_kwh) || 0,
        ca_percent_kl:  parseFloat(editForm.ca_percent_kl)  || 0,
      });
      setShowEditModal(false);
      await loadData();
      if (expandedPocketId === editingPocketId) {
        await loadPocketDetail(editingPocketId);
      }
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update pocket');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddMeters = async () => {
    setShowAddMetersModal(true);
    setSelectedMeterIds(new Set());
    setMeterSearch('');
    try {
      setLoadingMeters(true);
      const res = await api.getBillableMetersV2();
      setAvailableMeters(res.data);
    } catch {
      setAvailableMeters([]);
    } finally {
      setLoadingMeters(false);
    }
  };

  const handleAddMeters = async () => {
    if (!expandedPocketId || selectedMeterIds.size === 0) return;
    try {
      setAddingMeters(true);
      await api.addPocketMeters(expandedPocketId, Array.from(selectedMeterIds));
      setShowAddMetersModal(false);
      await loadPocketDetail(expandedPocketId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add meters');
    } finally {
      setAddingMeters(false);
    }
  };

  const handleRemoveMeter = async (meterId: string) => {
    if (!expandedPocketId) return;
    try {
      setRemovingMeterId(meterId);
      await api.removePocketMeter(expandedPocketId, meterId);
      await loadPocketDetail(expandedPocketId);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove meter');
    } finally {
      setRemovingMeterId(null);
    }
  };

  const toggleMeterSelection = (meterId: string) => {
    setSelectedMeterIds(prev => {
      const next = new Set(prev);
      if (next.has(meterId)) next.delete(meterId);
      else next.add(meterId);
      return next;
    });
  };

  // A meter can only live in one pocket. Hide meters already assigned to
  // *another* pocket; meters assigned to THIS pocket also don't need to show
  // (they're already in the list above the modal). Server-side validation in
  // POST /pockets/:id/meters is the safety net.
  const filteredMeters = availableMeters.filter(m => {
    if (m.assigned_pocket_id) return false;
    const q = meterSearch.toLowerCase();
    return m.meter_name.toLowerCase().includes(q) || m.meter_asset_id.toLowerCase().includes(q) || m.tariff_name.toLowerCase().includes(q);
  });

  // Serial/Name is the sole primary column — this table only has 3 data
  // fields to begin with (Asset ID, Serial/Name, Assigned Tariff) and
  // Assigned Tariff is a Badge (never primary — DataTable's collapsing
  // branch has no overflow-x-auto fallback below lg). That leaves Serial/
  // Name (a human-readable identity) and Meter Asset ID (an opaque UUID) as
  // candidates for the 2-column ceiling; Asset ID is reference/debug info a
  // property manager rarely needs at a glance, so it's kept secondary
  // instead of stretching to fill the ceiling for its own sake. rowActions
  // carries the existing "Remove" button. Array order matches the ORIGINAL
  // header order (Asset ID, then Serial/Name) — priority is independent of
  // position; grouping primaries first here would silently reorder the
  // desktop table.
  const pocketMeterColumns: DataTableColumn<PocketMeter>[] = [
    {
      key: 'assetId',
      label: 'Meter Asset ID',
      priority: 'secondary',
      render: (meter) => <span className="font-mono text-sm text-content">{meter.meter_asset_id}</span>,
    },
    {
      key: 'serial',
      label: 'Serial / Name',
      priority: 'primary',
      render: (meter) => <span className="text-sm text-content">{meter.meter_serial || meter.meter_name || '-'}</span>,
    },
    {
      key: 'tariff',
      label: 'Assigned Tariff',
      priority: 'secondary',
      render: (meter) => (
        meter.tariff_name ? (
          <Badge tone="success">{meter.tariff_name}</Badge>
        ) : (
          <Badge tone="danger">No tariff</Badge>
        )
      ),
    },
  ];

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-border-token rounded w-64"></div>
          <div className="h-48 bg-border-token rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Breadcrumb */}
      <div className="mb-4">
        <Link to="/pockets" className="text-accent hover:opacity-90 text-sm">
          &larr; Back to Buildings
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content">{buildingName}</h1>
          <p className="text-content-muted mt-1">
            {pockets.length} pocket{pockets.length !== 1 ? 's' : ''} | {totalMeters} meter{totalMeters !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors font-medium"
        >
          Create Pocket
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-token mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('report')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'report' ? 'border-primary-500 text-accent' : 'border-transparent text-content-muted hover:text-content hover:border-border-token'
            }`}
          >
            Report
          </button>
          <button
            onClick={() => setActiveTab('pockets')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pockets' ? 'border-primary-500 text-accent' : 'border-transparent text-content-muted hover:text-content hover:border-border-token'
            }`}
          >
            Pockets
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings' ? 'border-primary-500 text-accent' : 'border-transparent text-content-muted hover:text-content hover:border-border-token'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Error */}
      {error && (
        <Alert tone="danger" className="mb-6">
          {error}
        </Alert>
      )}

      {/* Report Tab */}
      {activeTab === 'report' && buildingId && (
        <>
          {/* I10: BuildingReport is DELIBERATELY untokenised literal-colour
              (it also feeds the customer PDF invoice via
              PrintBuildingReport.tsx — see the exemption comment at the top
              of components/BuildingReport.tsx) while ReportSchedulePanel
              right below it is fully dark-mode tokenised. That's an
              intentional seam, not an oversight: BuildingReportView's root
              now carries an explicit literal `color` (the C1 fix) so it
              renders as a self-consistent fixed-light slab regardless of
              this page's theme, rather than a half-migrated one. Do not
              "finish" tokenising it — that breaks the PDF. */}
          <BuildingReport buildingId={buildingId} />
          <ReportSchedulePanel buildingId={buildingId} buildingName={buildingName} />
        </>
      )}

      {/* Pockets Tab */}
      {activeTab === 'pockets' && (
        <div className="space-y-4">
          {/* CA % summary — per-utility, since electricity and water shares
              are now configured independently. Each total should reach
              ~100% when fully allocated. */}
          {(totalCaPercentKwh > 0 || totalCaPercentKl > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              {([['Electricity', totalCaPercentKwh, 'kWh'], ['Water', totalCaPercentKl, 'kL']] as const).map(([label, total, unit]) => (
                <div key={label} className={`px-4 py-2 rounded-lg ${
                  total === 0 ? 'bg-surface-sunken text-content-muted' :
                  Math.abs(total - 100) < 0.1 ? 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100' :
                  'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-100'
                }`}>
                  CA allocation ({label} / {unit}): <span className="font-semibold">{total.toFixed(1)}%</span> assigned
                  {total > 0 && Math.abs(total - 100) >= 0.1 &&
                    ` — ${total < 100 ? 'under' : 'over'}-allocated by ${Math.abs(total - 100).toFixed(1)}%`}
                </div>
              ))}
            </div>
          )}

          {pockets.length === 0 ? (
            <Card className="shadow-sm p-12 text-center text-content-muted">
              <p className="text-lg font-medium mb-1">No pockets for this building</p>
              <p className="text-sm">Create a pocket to group meters for billing.</p>
            </Card>
          ) : (
            pockets.map(pocket => (
              <Card key={pocket.id} className="shadow-sm overflow-hidden">
                {/* Pocket Header */}
                <button
                  onClick={() => handleTogglePocket(pocket)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-surface-sunken transition-colors"
                >
                  <div className="text-left">
                    <div className="flex items-center gap-3">
                      <h3 className="font-semibold text-content">{pocket.name}</h3>
                      <Badge tone="success">
                        {pocket.meter_count ?? 0} meter{(pocket.meter_count ?? 0) !== 1 ? 's' : ''}
                      </Badge>
                      {(() => {
                        const kwh = pocket.ca_percent_kwh ?? pocket.ca_percent ?? 0;
                        const kl  = pocket.ca_percent_kl  ?? pocket.ca_percent ?? 0;
                        if (kwh === 0 && kl === 0) return null;
                        // Always show both utilities so the user can see at a
                        // glance that water is also configured (or that it
                        // isn't). Earlier collapsed-when-equal display made
                        // the water column invisible after migration backfill.
                        return (
                          <Badge tone="warning" title="Common Area allocation per utility">
                            <span className="inline-flex items-center gap-1">
                              <span>CA:</span>
                              <span>{kwh}% kWh</span>
                              <span className="text-amber-400">·</span>
                              <span>{kl}% kL</span>
                            </span>
                          </Badge>
                        );
                      })()}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${
                        pocket.is_occupied ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          pocket.is_occupied ? 'bg-green-500' : 'bg-amber-500'
                        }`} />
                        {pocket.is_occupied ? (pocket.active_tenant_name ?? 'Occupied') : 'Vacant'}
                      </span>
                      {pocket.shop_no && <p className="text-xs text-content-muted">Unit: {pocket.shop_no}</p>}
                      {pocket.account_number && <p className="text-xs text-content-muted">Account: {pocket.account_number}</p>}
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-content-muted transition-transform ${expandedPocketId === pocket.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Expanded Detail */}
                {expandedPocketId === pocket.id && (
                  <div className="border-t border-border-token p-6">
                    {loadingDetail ? (
                      <div className="animate-pulse space-y-2">
                        {[1, 2, 3].map(i => <div key={i} className="h-8 bg-border-token rounded"></div>)}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="font-medium text-content">Meters in this pocket</h4>
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleOpenEditPocket(pocket)} className="px-3 py-1.5 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token font-medium">
                              Edit Pocket
                            </button>
                            <button onClick={handleOpenAddMeters} className="px-3 py-1.5 text-sm bg-accent text-accent-fg rounded-lg hover:opacity-90 font-medium">
                              Add Meters
                            </button>
                          </div>
                        </div>

                        {!expandedPocket?.meters || expandedPocket.meters.length === 0 ? (
                          <div className="text-center py-8 text-content-muted">
                            <p className="text-sm">No meters in this pocket yet.</p>
                          </div>
                        ) : (
                          <DataTable
                            data={expandedPocket.meters}
                            columns={pocketMeterColumns}
                            rowKey={(meter) => meter.id}
                            /* `lg:min-h-0`: 44px is a TOUCH floor; in a table
                               row it drives row height and costs
                               rows-per-screen on desktop. Full rationale on
                               Tenants.tsx's rowActions; escape documented at
                               tailwind.config.js:103. */
                            rowActions={(meter) => (
                              <button
                                onClick={() => handleRemoveMeter(meter.meter_asset_id)}
                                disabled={removingMeterId === meter.meter_asset_id}
                                className="tap-target lg:min-h-0 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm disabled:opacity-50"
                              >
                                {removingMeterId === meter.meter_asset_id ? 'Removing...' : 'Remove'}
                              </button>
                            )}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>
      )}

      {/* Settings Tab — Common Area meter registration */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <Card className="shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-content">Common Area Meters</h3>
                <p className="text-sm text-content-muted mt-1">
                  Register the building's bulk/CA meters here. Pockets containing a registered CA
                  meter are classified as <span className="font-medium text-purple-700 dark:text-purple-400">Supply</span> on
                  the report, and their consumption is allocated to tenant pockets via each pocket's
                  CA % setting.
                </p>
              </div>
              <button
                onClick={openCaAddModal}
                className="px-4 py-2 bg-accent text-accent-fg text-sm rounded-lg hover:opacity-90 whitespace-nowrap"
              >
                + Add CA Meter
              </button>
            </div>

            {caError && (
              <Alert tone="danger" className="mb-4">
                {caError}
              </Alert>
            )}

            {caLoading ? (
              <div className="py-8 text-center text-sm text-content-muted">Loading…</div>
            ) : caMeters.length === 0 ? (
              <div className="py-10 text-center border-2 border-dashed border-border-token rounded-lg">
                <p className="text-sm text-content-muted mb-1">No common-area meters registered yet.</p>
                <p className="text-xs text-content-muted">Add one to enable CA allocation and Supply classification.</p>
              </div>
            ) : (
              /* Common-area meters: 4 columns (Meter, Asset ID, Description,
                 Remove) — the review's count of 5 was one high. In the 3-5
                 judgment band, and ruled YES `min-w-max` despite being under
                 the >=6 threshold, on content rather than count: `px-4` on
                 every cell spends ~128px of a ~343px viewport before a glyph
                 is drawn, and the Asset ID column holds a full OpenRemote
                 asset UUID in `font-mono` (~22 chars of unbreakable text
                 between hyphens) next to a free-text Description. Those two
                 cannot both fit in the ~160px left once the Remove action is
                 placed; without `min-w-max` they word-wrap into slivers and
                 the scroller never engages. The opposite call to
                 BillVerification's 4-column line-items table, which is prose
                 that wraps acceptably — see the note there. Rule and
                 mechanism: DataTable.tsx:104-121. */
              <div className="overflow-x-auto">
                <table className="w-full min-w-max text-sm">
                  <thead className="bg-surface-sunken">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-muted uppercase">Meter</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-muted uppercase">Asset ID</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-content-muted uppercase">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-content-muted uppercase"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-token">
                    {caMeters.map(m => (
                      <tr key={m.id} className="hover:bg-surface-sunken">
                        <td className="px-4 py-2 font-medium">{m.meter_name || '—'}</td>
                        <td className="px-4 py-2 font-mono text-xs text-content-muted">{m.common_meter_asset_id}</td>
                        <td className="px-4 py-2 text-content-muted">{m.description || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => handleRemoveCaMeter(m.id)}
                            disabled={caRemovingId === m.id}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs disabled:opacity-50"
                          >
                            {caRemovingId === m.id ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* CA % allocation summary — link the two settings together so the
              admin can verify both are configured before approving a bill run. */}
          <Card className="shadow-sm p-6">
            <h3 className="text-lg font-semibold text-content mb-2">CA Distribution</h3>
            <p className="text-sm text-content-muted mb-3">
              Each tenant pocket has a separate CA% for electricity (kWh) and water (kL) — set in the Pockets tab.
              Each utility should sum to 100% across pockets.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {([['Electricity', totalCaPercentKwh, 'kWh'], ['Water', totalCaPercentKl, 'kL']] as const).map(([label, total, unit]) => (
                <div key={label} className={`px-4 py-3 rounded-lg text-sm ${
                  total === 0 ? 'bg-surface-sunken text-content-muted' :
                  Math.abs(total - 100) < 0.1 ? 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-100' :
                  'bg-amber-50 text-amber-700 dark:bg-amber-900 dark:text-amber-100'
                }`}>
                  <div className="font-medium mb-0.5">{label} ({unit})</div>
                  <div>Allocated: <span className="font-semibold">{total.toFixed(1)}%</span>
                    {total === 0 && ' — no allocations set yet'}
                    {total > 0 && Math.abs(total - 100) >= 0.1 &&
                      ` — ${total < 100 ? 'under' : 'over'}-allocated by ${Math.abs(total - 100).toFixed(1)}%`}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Add CA Meter Modal */}
      {caAddOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-2xl p-6 max-h-[90vh] flex flex-col">
            <h2 className="text-lg font-bold text-content mb-2">Register Common Area Meter</h2>
            <p className="text-sm text-content-muted mb-4">
              Pick the bulk/CA meter for <span className="font-medium">{buildingName}</span>.
            </p>

            <input
              type="text"
              placeholder="Search meters by name or asset id…"
              value={caSearch}
              onChange={e => setCaSearch(e.target.value)}
              className="w-full px-3 py-2 border border-border-token rounded-lg mb-3 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
            />

            <div className="flex-1 overflow-y-auto border border-border-token rounded-lg mb-4 min-h-[200px]">
              {caFilteredMeters.length === 0 ? (
                <div className="p-6 text-center text-sm text-content-muted">
                  {caSearch ? 'No meters match your search.' : 'No eligible meters available.'}
                </div>
              ) : (
                <ul className="divide-y divide-border-token">
                  {caFilteredMeters.map(m => (
                    <li key={m.meter_asset_id}>
                      <label className="flex items-center px-4 py-3 hover:bg-surface-sunken cursor-pointer">
                        <input
                          type="radio"
                          name="ca-meter"
                          checked={caSelectedMeterId === m.meter_asset_id}
                          onChange={() => setCaSelectedMeterId(m.meter_asset_id)}
                          className="mr-3 text-accent"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-content truncate">{m.meter_name}</div>
                          <div className="text-xs text-content-muted font-mono truncate">{m.meter_asset_id}</div>
                          {m.assigned_pocket_name && (
                            <div className="text-xs text-content-muted mt-0.5">In pocket: {m.assigned_pocket_name}</div>
                          )}
                        </div>
                        <MeterTypeIcon type={m.meter_type} className="w-5 h-5 text-content-muted ml-2" />
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-content mb-1">Description (optional)</label>
              <input
                type="text"
                placeholder='e.g. "Main bulk supply" or "Common area lighting"'
                value={caDescription}
                onChange={e => setCaDescription(e.target.value)}
                className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setCaAddOpen(false)}
                className="px-4 py-2 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCaMeter}
                disabled={!caSelectedMeterId || caSaving}
                className="px-4 py-2 text-sm bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {caSaving ? 'Saving…' : 'Register'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Pocket Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-4">Create Pocket</h2>
            {createError && <Alert tone="danger" className="mb-4">{createError}</Alert>}
            <form onSubmit={handleCreatePocket}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Pocket Name *</label>
                  <input type="text" required value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Unit / Shop No</label>
                  <input type="text" value={createForm.shop_no} onChange={e => setCreateForm(p => ({ ...p, shop_no: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Description</label>
                  <textarea value={createForm.description} onChange={e => setCreateForm(p => ({ ...p, description: e.target.value }))} rows={2}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button type="submit" disabled={creating} className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50">
                  {creating ? 'Creating...' : 'Create Pocket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Pocket Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-content mb-4">Edit Pocket</h2>
            {editError && <Alert tone="danger" className="mb-4">{editError}</Alert>}
            <form onSubmit={handleSaveEdit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Pocket Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Unit / Shop No</label>
                  <input type="text" value={editForm.shop_no} onChange={e => setEditForm(p => ({ ...p, shop_no: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
                <p className="text-xs text-content-muted -mt-2">Tenant assignment is managed on the <a href="/tenants" className="text-accent hover:underline">Tenants</a> page.</p>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Account Number</label>
                  <input type="text" value={editForm.account_number} onChange={e => setEditForm(p => ({ ...p, account_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Municipal/utility account number" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Common Area %</label>
                  <p className="text-xs text-content-muted mb-2">Independent shares for electricity and water — set per the tenant's expected use of each.</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-content-muted mb-1">Electricity (kWh)</label>
                      <input type="number" step="0.1" min="0" max="100"
                        value={editForm.ca_percent_kwh}
                        onChange={e => setEditForm(p => ({ ...p, ca_percent_kwh: e.target.value }))}
                        className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-content-muted mb-1">Water (kL)</label>
                      <input type="number" step="0.1" min="0" max="100"
                        value={editForm.ca_percent_kl}
                        onChange={e => setEditForm(p => ({ ...p, ca_percent_kl: e.target.value }))}
                        className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="0" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Description</label>
                  <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} rows={2}
                    className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Meters Modal */}
      {showAddMetersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-2xl p-6 max-h-[80vh] flex flex-col">
            <h2 className="text-lg font-bold text-content mb-4">Add Meters to Pocket</h2>
            <input type="text" placeholder="Search meters..." value={meterSearch} onChange={e => setMeterSearch(e.target.value)}
              className="w-full px-3 py-2 border border-border-token rounded-lg mb-4 focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
            <div className="overflow-y-auto flex-1 mb-4">
              {loadingMeters ? (
                <div className="text-center py-8 text-content-muted">Loading meters...</div>
              ) : filteredMeters.length === 0 ? (
                <div className="text-center py-8 text-content-muted">No meters found</div>
              ) : (
                <div className="space-y-2">
                  {filteredMeters.map(meter => {
                    const alreadyInPocket = expandedPocket?.meters?.some(m => m.meter_asset_id === meter.meter_asset_id);
                    return (
                      // C3 (dark-mode contrast fix): the selected branch used a
                      // hand-rolled `bg-primary-50` with no dark: counterpart
                      // (1.00:1 collapse in dark mode, since bg-primary-50
                      // doesn't change but text-content does).
                      //
                      // Tried first: blanket-replacing with `bg-row-selected`
                      // (as suggested — it's the existing DataTable/
                      // CreateBillRunDialog selected-row token, already
                      // contrast-registered for `text`). Rejected on
                      // computation: `text-content-muted` (the meter_asset_id
                      // caption below) against `rowSelected` LIGHT is only
                      // 4.17:1 — below AA — versus 4.60:1 against the current
                      // `bg-primary-50`. A blanket swap would have fixed dark
                      // mode by *regressing* light mode from a thin pass to an
                      // outright fail.
                      //
                      // Fix instead scopes the token to dark only:
                      // `dark:bg-row-selected` (specificity 0,2,0, beats the
                      // unscoped `bg-primary-50` at 0,1,0 once `.dark` is on
                      // <html>, same mechanism as this codebase's
                      // `bg-amber-50 dark:bg-amber-900` convention) — light
                      // mode is untouched (still the pre-existing 4.60:1/
                      // 16.77:1), dark mode gets rowSelected's dark value
                      // (#1e2c28): text-content 13.90:1, text-content-muted
                      // 5.76:1 — both clear AA. `border-primary-300` is left
                      // unprefixed: a bright teal (#5eead4) that reads clearly
                      // against rowSelected(dark) at 9.82:1, so it doesn't
                      // need a separate dark shade. See
                      // src/test/contrast.test.ts for the registered pairs.
                      <label key={meter.meter_asset_id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        alreadyInPocket ? 'bg-surface-sunken border-border-token opacity-50' : selectedMeterIds.has(meter.meter_asset_id) ? 'bg-primary-50 dark:bg-row-selected border-primary-300' : 'bg-surface-raised border-border-token hover:bg-surface-sunken'
                      }`}>
                        <input type="checkbox" disabled={alreadyInPocket} checked={selectedMeterIds.has(meter.meter_asset_id)}
                          onChange={() => toggleMeterSelection(meter.meter_asset_id)} className="rounded border-border-token text-accent focus:ring-primary-500" />
                        <MeterTypeIcon type={meter.meter_type} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-content truncate">{meter.meter_name}</p>
                          <p className="text-xs text-content-muted font-mono">{meter.meter_asset_id}</p>
                        </div>
                        <Badge tone={meter.has_tariff ? 'success' : 'neutral'}>
                          {meter.tariff_name || 'No tariff'}
                        </Badge>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-content-muted">{selectedMeterIds.size} selected</span>
              <div className="flex gap-3">
                <button onClick={() => setShowAddMetersModal(false)} className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token">Cancel</button>
                <button onClick={handleAddMeters} disabled={addingMeters || selectedMeterIds.size === 0}
                  className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50">
                  {addingMeters ? 'Adding...' : `Add ${selectedMeterIds.size} Meter${selectedMeterIds.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
